import {useCallback} from 'react'
import {useController, useFormContext} from 'react-hook-form'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {InputSelectPathology} from '@/features/projects/components/input-select-pathology/InputSelectPathology'
import {getDiagnosisSelectionVersion, getFirstMappedCode} from '@/features/projects/diagnosisVersion'
import {OtherPathologiesFormSchema} from '@/features/projects/formSchemas'
import {Diagnosis} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'

interface ProjectOtherPathologiesFieldsProps {
    index: number
    disabledIcd9: string[]
    disabledIcd10: string[]
    showTrashButton: boolean
    remove: (index: number) => void
}

export const ProjectOtherPathologiesFields = ({
    index,
    disabledIcd9,
    disabledIcd10,
    showTrashButton,
    remove
}: ProjectOtherPathologiesFieldsProps) => {
    const {t} = useTranslation()
    const {control} = useFormContext<OtherPathologiesFormSchema>()
    const {field} = useController({control, name: `otherPathologies.${index}`})

    const isIcd9Selectable = getDiagnosisSelectionVersion() == 'ICD9'

    // Same rule as the main pathologies: two independent fields, and the ICD-10 is only proposed
    // from the transcoding while it is still empty.
    const handleSelectIcd9 = useCallback(
        (value: Diagnosis | null) => {
            field.onChange({
                ...field.value,
                diagnosis: value,
                icd10Diagnosis: field.value.icd10Diagnosis ?? getFirstMappedCode(value)
            })
        },
        [field.onChange, field.value]
    )

    const handleSelectIcd10 = useCallback(
        (value: Diagnosis | null) => {
            field.onChange({...field.value, icd10Diagnosis: value})
        },
        [field.onChange, field.value]
    )

    return (
        <Flexbox fullWidth justify="space-between" gap={2.5} align="flex-end">
            <div style={{flex: '1 1 0', minWidth: 0}}>
                <InputSelectPathology
                    label={t('projects:pathology_number_icd9_cm', {number: index + 1})}
                    placeholder={t('projects:pathology_number_placeholder')}
                    value={field.value.diagnosis}
                    onChange={handleSelectIcd9}
                    disabledOptions={disabledIcd9}
                    disabled={!isIcd9Selectable}
                    version="ICD9"
                />
            </div>
            <div style={{flex: '1 1 0', minWidth: 0}}>
                <InputSelectPathology
                    label={t('projects:pathology_number_icd10_im', {number: index + 1})}
                    placeholder={t('projects:pathology_number_icd10_im_placeholder')}
                    value={field.value.icd10Diagnosis}
                    onChange={handleSelectIcd10}
                    disabledOptions={disabledIcd10}
                    version="ICD10"
                />
            </div>
            {showTrashButton && (
                <Button variant="ghost" shape="square" onClick={() => remove(index)}>
                    <Trash01Icon />
                </Button>
            )}
        </Flexbox>
    )
}
